// Sorteos (pedido de Tuto 2026-10-30): registrar los boletos físicos que se
// venden a mano, cobrar con link de pago (SMS + email) y ver el mapa de
// boletos / reporte. La venta en línea sale por /sorteo?r=TOKEN.
import { useEffect, useState, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import moment from 'moment';
import {
  Card, CardContent, CardHeader, Table, Stack, Button, Container, Typography, TableBody, TableCell, TableHead, TableRow,
  TableContainer, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Switch, FormControlLabel, Chip,
  Box, Grid, CircularProgress, Tabs, Tab, MenuItem, Tooltip, Alert, Snackbar, Divider, ToggleButtonGroup, ToggleButton, Checkbox,
} from '@mui/material';
import { getFirestore, collection, getDocs, addDoc, updateDoc, doc, query, where, Timestamp } from 'firebase/firestore';
import Iconify from '../components/iconify';
import TicketGallery from '../components/raffle-ticket/TicketGallery';
import { getCurrentIdentity } from '../protectedRoute';
import {
  RAFFLES_COLLECTION, SALES_COLLECTION, TICKETS_COLLECTION, FN_URLS, raffleUrl, saleUrl,
  SALE_STATUS_LABELS, SALE_STATUS_COLORS, METHOD_LABELS, SOURCE_LABELS, money, fmtDateEs, numbersLabel, parseNumbers,
  randomToken, callFn, downloadCsv, EMPTY_RAFFLE, fileToBase64,
} from '../utils/raffles';

const db = getFirestore();
const toDate = (ts) => (ts && ts.toDate ? ts.toDate() : ts ? new Date(ts) : null);

const emptySale = { numbersText: '', name: '', phone: '', email: '', mode: 'paid', method: 'cash', note: '', amount: '' };

export default function RafflesPage() {
  const [raffles, setRaffles] = useState([]);
  const [raffleId, setRaffleId] = useState('');
  const [sales, setSales] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSales, setLoadingSales] = useState(false);
  const [tab, setTab] = useState(0);
  const [toast, setToast] = useState(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_RAFFLE);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const uploadPrizeImage = async (file) => {
    if (!file) return;
    setUploadingImage(true);
    try {
      const dataBase64 = await fileToBase64(file);
      const { url } = await callFn(FN_URLS.uploadImage, { fileName: file.name, contentType: file.type, dataBase64 });
      setForm((f) => ({ ...f, imageUrl: url }));
    } catch (e) {
      setToast({ severity: 'error', message: e.message });
    } finally {
      setUploadingImage(false);
    }
  };

  const [saleOpen, setSaleOpen] = useState(false);
  const [sale, setSale] = useState(emptySale);
  const [busyId, setBusyId] = useState(null);
  const [payDialog, setPayDialog] = useState(null);
  const [payForm, setPayForm] = useState({ method: 'cash', note: '', amount: '' });
  const [detail, setDetail] = useState(null);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  // Tómbola: filtro (all | no | yes) y si se avisa al comprador al marcar.
  const [filterDeposited, setFilterDeposited] = useState('all');
  const [notifyDeposit, setNotifyDeposit] = useState(() => { try { return localStorage.getItem('raffleNotifyDeposit') !== '0'; } catch { return true; } });

  const raffle = useMemo(() => raffles.find((r) => r.id === raffleId) || null, [raffles, raffleId]);

  const loadRaffles = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, RAFFLES_COLLECTION));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (toDate(b.createdAt) || 0) - (toDate(a.createdAt) || 0));
      setRaffles(list);
      setRaffleId((cur) => cur && list.some((r) => r.id === cur) ? cur : (list.find((r) => r.status === 'active') || list[0] || {}).id || '');
    } catch (e) {
      setToast({ severity: 'error', message: `No se pudo cargar: ${e.message}` });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSales = useCallback(async () => {
    if (!raffleId) { setSales([]); setTickets([]); return; }
    setLoadingSales(true);
    try {
      const [sSnap, tSnap] = await Promise.all([
        getDocs(query(collection(db, SALES_COLLECTION), where('raffleId', '==', raffleId))),
        getDocs(query(collection(db, TICKETS_COLLECTION), where('raffleId', '==', raffleId))),
      ]);
      setSales(sSnap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (toDate(b.createdAt) || 0) - (toDate(a.createdAt) || 0)));
      setTickets(tSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      setToast({ severity: 'error', message: e.message });
    } finally {
      setLoadingSales(false);
    }
  }, [raffleId]);

  useEffect(() => { loadRaffles(); }, [loadRaffles]);
  useEffect(() => { loadSales(); }, [loadSales]);

  // ---------- Sorteo ----------
  const openNew = () => { setEditing(null); setForm(EMPTY_RAFFLE); setEditorOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      ...EMPTY_RAFFLE, ...r,
      ticketPrice: String(r.ticketPrice ?? ''), totalTickets: String(r.totalTickets ?? ''),
      onlineFrom: r.onlineFrom ? String(r.onlineFrom) : '', onlineTo: r.onlineTo ? String(r.onlineTo) : '',
      onlineEnabled: r.onlineEnabled !== false,
      prizeValue: r.prizeValue ? String(r.prizeValue) : '',
    });
    setEditorOpen(true);
  };
  const saveRaffle = async () => {
    if (!form.name.trim()) { setToast({ severity: 'warning', message: 'Escribe el nombre del sorteo.' }); return; }
    const total = Number(form.totalTickets); const price = Number(form.ticketPrice);
    if (!(total > 0) || !(price > 0)) { setToast({ severity: 'warning', message: 'Cantidad de boletos y precio deben ser mayores que 0.' }); return; }
    const from = Number(form.onlineFrom) || 0; const to = Number(form.onlineTo) || 0;
    if ((from || to) && !(from >= 1 && to >= from && to <= total)) { setToast({ severity: 'warning', message: `El rango en línea debe estar entre 1 y ${total}.` }); return; }
    setSaving(true);
    try {
      const data = {
        name: form.name.trim(), prize: form.prize.trim(), description: form.description.trim(), drawDate: form.drawDate || '',
        venue: form.venue.trim(), ticketPrice: price, totalTickets: total, onlineFrom: from, onlineTo: to,
        onlineEnabled: form.onlineEnabled !== false, terms: form.terms, rulesEs: (form.rulesEs || '').trim(), rulesEn: (form.rulesEn || '').trim(),
        contactName: form.contactName, contactPhone: form.contactPhone,
        contactEmail: form.contactEmail, status: form.status || 'active', updatedAt: Timestamp.now(),
        ticketBrand: (form.ticketBrand || '').trim(), ticketHeadline: (form.ticketHeadline || '').trim() || 'BOLETO SORTEO',
        ticketFooter: (form.ticketFooter || '').trim(), prizeValue: Number(form.prizeValue) || 0, imageUrl: form.imageUrl || '',
      };
      if (editing) await updateDoc(doc(db, RAFFLES_COLLECTION, editing.id), data);
      else {
        const ref = await addDoc(collection(db, RAFFLES_COLLECTION), { ...data, token: randomToken(), createdAt: Timestamp.now(), createdBy: getCurrentIdentity().email || '' });
        setRaffleId(ref.id);
      }
      setEditorOpen(false);
      setToast({ severity: 'success', message: editing ? 'Sorteo actualizado.' : 'Sorteo creado.' });
      await loadRaffles();
    } catch (e) {
      setToast({ severity: 'error', message: e.message });
    } finally {
      setSaving(false);
    }
  };

  const copy = async (url, label = 'Link copiado.') => {
    try { await navigator.clipboard.writeText(url); setToast({ severity: 'success', message: label }); }
    catch { window.prompt('Copia el link:', url); }
  };

  // ---------- Venta ----------
  const openSale = (numbers = []) => {
    setSale({ ...emptySale, numbersText: numbers.join(', ') });
    setSaleOpen(true);
  };
  const saleNumbers = useMemo(() => parseNumbers(sale.numbersText), [sale.numbersText]);
  const saleAmount = sale.amount !== '' ? Number(sale.amount) : saleNumbers.length * (Number(raffle?.ticketPrice) || 0);
  const submitSale = async () => {
    if (!saleNumbers.length) { setToast({ severity: 'warning', message: 'Escribe los números de boleto (p. ej. 312, 315-318).' }); return; }
    if (sale.mode === 'link' && !sale.phone.trim() && !sale.email.trim()) { setToast({ severity: 'warning', message: 'Para enviar el link hace falta teléfono o email.' }); return; }
    setSaving(true);
    try {
      const r = await callFn(FN_URLS.adminSale, {
        raffleId, numbers: saleNumbers,
        buyer: { name: sale.name.trim(), phone: sale.phone.trim(), email: sale.email.trim() },
        paid: sale.mode === 'paid', method: sale.method, note: sale.note, amount: sale.amount !== '' ? Number(sale.amount) : undefined,
        recordedBy: getCurrentIdentity().email || '',
      });
      const sent = r.link ? [r.link.smsSent && 'SMS', r.link.emailSent && 'email'].filter(Boolean).join(' y ') : '';
      setToast({ severity: 'success', message: sale.mode === 'paid' ? `Boletos ${numbersLabel(r.numbers)} registrados como vendidos.` : `Boletos ${numbersLabel(r.numbers)} reservados${sent ? ` · link enviado por ${sent}` : ''}.` });
      setSaleOpen(false);
      await loadSales();
    } catch (e) {
      setToast({ severity: 'error', message: e.message });
    } finally {
      setSaving(false);
    }
  };

  const sendLink = async (s) => {
    setBusyId(s.id);
    try {
      const r = await callFn(FN_URLS.sendLink, { saleId: s.id });
      const sent = [r.smsSent && 'SMS', r.emailSent && 'email'].filter(Boolean).join(' y ');
      setToast(sent ? { severity: 'success', message: `Link enviado por ${sent}.` } : { severity: 'error', message: `No se envió: ${r.smsError || r.emailError || 'sin teléfono ni email'}` });
      await loadSales();
    } catch (e) { setToast({ severity: 'error', message: e.message }); }
    finally { setBusyId(null); }
  };
  const resendReceipt = async (s) => {
    setBusyId(s.id);
    try {
      const r = await callFn(FN_URLS.resendReceipt, { saleId: s.id });
      const sent = [r.smsSent && 'SMS', r.emailSent && 'email'].filter(Boolean).join(' y ');
      setToast(sent ? { severity: 'success', message: `Boletos reenviados por ${sent}.` } : { severity: 'error', message: `No se envió: ${r.smsError || r.emailError || 'sin teléfono ni email'}` });
    } catch (e) { setToast({ severity: 'error', message: e.message }); }
    finally { setBusyId(null); }
  };
  const cancelSale = async (s) => {
    if (!window.confirm(`¿Cancelar la venta de ${numbersLabel(s.numbers)} (${s.buyerName || 'sin nombre'})? Los números quedan libres otra vez.`)) return;
    const note = window.prompt('Nota (opcional): motivo, reembolso…', '') || '';
    setBusyId(s.id);
    try {
      await callFn(FN_URLS.cancelSale, { saleId: s.id, note, recordedBy: getCurrentIdentity().email || '' });
      setToast({ severity: 'success', message: 'Venta cancelada; números liberados.' });
      setDetail(null);
      await loadSales();
    } catch (e) { setToast({ severity: 'error', message: e.message }); }
    finally { setBusyId(null); }
  };
  // Marcar / desmarcar que el boleto físico ya se echó en la tómbola.
  const toggleDeposited = async (s) => {
    const deposited = !s.depositedAt;
    if (!deposited && !window.confirm(`¿Quitar la marca de "en la tómbola" a ${numbersLabel(s.numbers)}?`)) return;
    setBusyId(s.id);
    try {
      const r = await callFn(FN_URLS.markDeposited, { saleId: s.id, deposited, notify: notifyDeposit, recordedBy: getCurrentIdentity().email || '' });
      const sent = r.notify ? [r.notify.smsSent && 'SMS', r.notify.emailSent && 'email'].filter(Boolean).join(' y ') : '';
      setToast({ severity: 'success', message: deposited ? `${numbersLabel(s.numbers)} en la tómbola${sent ? ` · aviso enviado por ${sent}` : notifyDeposit && r.notify ? ' · no se pudo avisar (sin teléfono ni email)' : ''}.` : 'Marca quitada.' });
      const patch = deposited ? { depositedAt: Timestamp.now(), depositedBy: getCurrentIdentity().email || '' } : { depositedAt: null, depositedBy: null };
      setSales((list) => list.map((x) => (x.id === s.id ? { ...x, ...patch } : x)));
      setDetail((d) => (d && d.id === s.id ? { ...d, ...patch } : d));
    } catch (e) { setToast({ severity: 'error', message: e.message }); }
    finally { setBusyId(null); }
  };
  const submitPay = async () => {
    setSaving(true);
    try {
      await callFn(FN_URLS.recordPayment, { saleId: payDialog.id, method: payForm.method, note: payForm.note, amount: payForm.amount !== '' ? Number(payForm.amount) : undefined, recordedBy: getCurrentIdentity().email || '' });
      setToast({ severity: 'success', message: 'Pago registrado y boletos enviados.' });
      setPayDialog(null);
      await loadSales();
    } catch (e) { setToast({ severity: 'error', message: e.message }); }
    finally { setSaving(false); }
  };

  // ---------- Derivados ----------
  const ticketMap = useMemo(() => { const m = {}; tickets.forEach((t) => { m[Number(t.number)] = t; }); return m; }, [tickets]);
  const stats = useMemo(() => {
    const total = Number(raffle?.totalTickets) || 0;
    const sold = tickets.filter((t) => t.status === 'sold').length;
    const held = tickets.filter((t) => t.status !== 'sold').length;
    const paidSales = sales.filter((s) => s.status === 'paid');
    const collected = paidSales.reduce((a, s) => a + (s.paidCents || s.amountCents || 0), 0);
    const deposited = paidSales.filter((s) => s.depositedAt).reduce((a, s) => a + (s.numbers || []).length, 0);
    const byMethod = {};
    paidSales.forEach((s) => (s.payments || []).forEach((p) => { byMethod[p.method] = (byMethod[p.method] || 0) + (p.amountCents || 0); }));
    const bySource = { admin: 0, online: 0 };
    paidSales.forEach((s) => { bySource[s.source === 'online' ? 'online' : 'admin'] += (s.numbers || []).length; });
    const dayMap = {};
    paidSales.forEach((s) => { const d = toDate(s.paidAt); if (d) { const k = moment(d).format('YYYY-MM-DD'); dayMap[k] = (dayMap[k] || 0) + (s.numbers || []).length; } });
    const from = Number(raffle?.onlineFrom) || 0; const to = Number(raffle?.onlineTo) || 0;
    let onlineAvailable = 0;
    if (from && to >= from) for (let n = from; n <= Math.min(to, total); n += 1) if (!ticketMap[n]) onlineAvailable += 1;
    return { total, sold, held, available: Math.max(0, total - sold - held), collected, byMethod, bySource, dayMap, onlineAvailable, deposited, expected: sold * Math.round((Number(raffle?.ticketPrice) || 0) * 100) };
  }, [raffle, tickets, sales, ticketMap]);

  const filteredSales = useMemo(() => {
    const q = search.trim().toLowerCase();
    const qn = /^#?\d+$/.test(q) ? Number(q.replace('#', '')) : null;
    return sales.filter((s) => {
      if (filterStatus !== 'all' && s.status !== filterStatus) return false;
      if (filterDeposited === 'no' && (s.status !== 'paid' || s.depositedAt)) return false;
      if (filterDeposited === 'yes' && !s.depositedAt) return false;
      if (qn != null) return (s.numbers || []).includes(qn);
      if (q && !`${s.buyerName} ${s.phone} ${s.email} ${s.note || ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [sales, search, filterStatus, filterDeposited]);

  const csvRows = useMemo(() => sales.filter((s) => s.status === 'paid' || s.status === 'pending').flatMap((s) => (s.numbers || []).map((n) => ({
    Boleto: n, Nombre: s.buyerName, Telefono: s.phone, Email: s.email, Estado: SALE_STATUS_LABELS[s.status], Origen: SOURCE_LABELS[s.source] || s.source,
    Tombola: s.depositedAt ? 'Sí' : 'No',
    Metodo: (s.payments || []).map((p) => METHOD_LABELS[p.method] || p.method).join(' | '), Monto_venta: (s.amountCents || 0) / 100,
    Fecha: s.paidAt ? moment(toDate(s.paidAt)).format('YYYY-MM-DD HH:mm') : (s.createdAt ? moment(toDate(s.createdAt)).format('YYYY-MM-DD HH:mm') : ''), Vendido_por: s.soldBy || '',
  }))).sort((a, b) => a.Boleto - b.Boleto), [sales]);

  const price = Number(raffle?.ticketPrice) || 0;

  return (
    <>
      <Helmet><title>Sorteos | BBTC</title></Helmet>
      <Container maxWidth="xl">
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={2} mb={3}>
          <Box>
            <Typography variant="h4">Sorteos</Typography>
            <Typography variant="body2" color="text.secondary">Boletos numerados: registra los que vendes a mano, cobra con link o en línea.</Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {raffles.length > 0 && (
              <TextField select size="small" label="Sorteo" value={raffleId} onChange={(e) => setRaffleId(e.target.value)} sx={{ minWidth: 240 }}>
                {raffles.map((r) => <MenuItem key={r.id} value={r.id}>{r.name}{r.status === 'closed' ? ' (cerrado)' : ''}</MenuItem>)}
              </TextField>
            )}
            <Button variant="outlined" onClick={openNew} startIcon={<Iconify icon="eva:plus-fill" />}>Nuevo sorteo</Button>
            {raffle && raffle.status !== 'closed' && <Button variant="contained" onClick={() => openSale()} startIcon={<Iconify icon="eva:pricetags-outline" />}>Vender boletos</Button>}
          </Stack>
        </Stack>

        {loading ? <Box textAlign="center" py={6}><CircularProgress /></Box> : !raffle ? (
          <Card><CardContent><Typography color="text.secondary" align="center" py={4}>No hay sorteos. Crea el primero con “Nuevo sorteo”.</Typography></CardContent></Card>
        ) : (
          <>
            <Grid container spacing={2} mb={2}>
              {[
                { label: 'Vendidos', value: `${stats.sold} / ${stats.total}`, sub: `${stats.held ? `${stats.held} reservados · ` : ''}${stats.available} libres` },
                { label: 'Cobrado', value: money(stats.collected), sub: Object.keys(stats.byMethod).map((m) => `${METHOD_LABELS[m] || m} ${money(stats.byMethod[m])}`).join(' · ') || '—' },
                { label: 'Mostrador / En línea', value: `${stats.bySource.admin} / ${stats.bySource.online}`, sub: raffle.onlineFrom ? `En línea: #${raffle.onlineFrom}–#${raffle.onlineTo} · ${stats.onlineAvailable} libres` : 'Sin rango en línea' },
                { label: 'Sorteo', value: raffle.drawDate ? fmtDateEs(raffle.drawDate) : '—', sub: `${money(price * 100)} por boleto${raffle.venue ? ` · ${raffle.venue}` : ''}` },
              ].map((k) => (
                <Grid item xs={6} md={3} key={k.label}>
                  <Card><CardContent sx={{ py: 2 }}>
                    <Typography variant="overline" color="text.secondary">{k.label}</Typography>
                    <Typography variant="h4">{k.value}</Typography>
                    <Typography variant="caption" color="text.secondary">{k.sub}</Typography>
                  </CardContent></Card>
                </Grid>
              ))}
            </Grid>

            <Card sx={{ mb: 2 }}><CardContent sx={{ py: 1.5 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  <strong>Página de venta en línea:</strong> <a href={raffleUrl(raffle.token)} target="_blank" rel="noreferrer">{raffleUrl(raffle.token)}</a>
                  {raffle.onlineEnabled === false && <Chip size="small" label="venta en línea apagada" color="warning" sx={{ ml: 1 }} />}
                  {!raffle.onlineFrom && <Chip size="small" label="falta el rango de boletos en línea" color="warning" sx={{ ml: 1 }} />}
                </Typography>
                <Button size="small" onClick={() => copy(raffleUrl(raffle.token))} startIcon={<Iconify icon="eva:copy-outline" />}>Copiar</Button>
                <Button size="small" onClick={() => openEdit(raffle)} startIcon={<Iconify icon="eva:edit-outline" />}>Editar sorteo</Button>
              </Stack>
            </CardContent></Card>

            <Card>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Tab label="Ventas" />
                <Tab label="Mapa de boletos" />
                <Tab label="Reporte" />
              </Tabs>

              {tab === 0 && (
                <CardContent>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} mb={2}>
                    <TextField size="small" label="Buscar (nombre, teléfono, email o # boleto)" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 300 }} />
                    <TextField size="small" select label="Estado" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} sx={{ minWidth: 180 }}>
                      <MenuItem value="all">Todos</MenuItem>
                      {Object.keys(SALE_STATUS_LABELS).map((k) => <MenuItem key={k} value={k}>{SALE_STATUS_LABELS[k]}</MenuItem>)}
                    </TextField>
                    <TextField size="small" select label="Tómbola" value={filterDeposited} onChange={(e) => setFilterDeposited(e.target.value)} sx={{ minWidth: 170 }}>
                      <MenuItem value="all">Todos</MenuItem>
                      <MenuItem value="no">Falta tirar (pagados)</MenuItem>
                      <MenuItem value="yes">Ya tirados</MenuItem>
                    </TextField>
                    <Tooltip title="Al marcar un boleto como tirado, se le avisa al comprador por email y SMS"><FormControlLabel sx={{ ml: 0 }} control={<Switch size="small" checked={notifyDeposit} onChange={(e) => { setNotifyDeposit(e.target.checked); try { localStorage.setItem('raffleNotifyDeposit', e.target.checked ? '1' : '0'); } catch { /* ignore */ } }} />} label={<Typography variant="body2">Avisar al tirar</Typography>} /></Tooltip>
                    <Box flex={1} />
                    <Button onClick={loadSales} startIcon={<Iconify icon="eva:refresh-outline" />}>Actualizar</Button>
                    <Button onClick={() => downloadCsv(`sorteo-${moment().format('YYYY-MM-DD')}.csv`, csvRows)} disabled={!csvRows.length} variant="outlined" startIcon={<Iconify icon="eva:download-outline" />}>CSV</Button>
                  </Stack>
                  {loadingSales ? <Box textAlign="center" py={4}><CircularProgress size={28} /></Box> : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead><TableRow>
                          <TableCell>Boletos</TableCell><TableCell>Comprador</TableCell><TableCell>Contacto</TableCell><TableCell align="right">Monto</TableCell>
                          <TableCell>Estado</TableCell><TableCell>Origen</TableCell><TableCell>Fecha</TableCell>
                          <TableCell align="center"><Tooltip title="Boleto físico ya depositado en la tómbola"><span>Tómbola</span></Tooltip></TableCell>
                          <TableCell align="right">Acciones</TableCell>
                        </TableRow></TableHead>
                        <TableBody>
                          {filteredSales.length === 0 && <TableRow><TableCell colSpan={9}><Typography color="text.secondary" align="center" py={3}>Sin ventas{sales.length ? ' con esos filtros' : ''}.</Typography></TableCell></TableRow>}
                          {filteredSales.map((s) => {
                            const busy = busyId === s.id;
                            const paid = s.status === 'paid';
                            return (
                              <TableRow key={s.id} hover sx={{ opacity: s.status === 'cancelled' || s.status === 'expired' ? 0.55 : 1 }}>
                                <TableCell><Typography variant="subtitle2" sx={{ cursor: 'pointer', color: 'error.main' }} onClick={() => setDetail(s)}>{numbersLabel(s.numbers)}</Typography><Typography variant="caption" color="text.secondary">{(s.numbers || []).length} boleto{(s.numbers || []).length !== 1 ? 's' : ''}</Typography></TableCell>
                                <TableCell><Typography variant="body2">{s.buyerName || '—'}</Typography>{s.note && <Typography variant="caption" color="text.secondary">{s.note}</Typography>}</TableCell>
                                <TableCell><Typography variant="caption" display="block">{s.phone}</Typography><Typography variant="caption" color="text.secondary">{s.email}</Typography></TableCell>
                                <TableCell align="right">{money(s.amountCents)}{paid && (s.payments || []).length > 0 && <Typography variant="caption" display="block" color="text.secondary">{(s.payments || []).map((p) => METHOD_LABELS[p.method] || p.method).join(', ')}</Typography>}</TableCell>
                                <TableCell><Chip size="small" label={SALE_STATUS_LABELS[s.status] || s.status} color={SALE_STATUS_COLORS[s.status] || 'default'} /></TableCell>
                                <TableCell><Typography variant="body2">{SOURCE_LABELS[s.source] || s.source}</Typography>{s.soldBy && <Typography variant="caption" color="text.secondary">{s.soldBy}</Typography>}</TableCell>
                                <TableCell><Typography variant="body2">{moment(toDate(s.paidAt || s.createdAt)).format('D/M/YY h:mm a')}</Typography></TableCell>
                                <TableCell align="center" sx={{ bgcolor: s.depositedAt ? '#e8f5e9' : undefined }}>
                                  {paid ? (
                                    <Tooltip title={s.depositedAt ? `En la tómbola desde ${moment(toDate(s.depositedAt)).format('D/M/YY h:mm a')}${s.depositedBy ? ` · ${s.depositedBy}` : ''}` : 'Marcar: boleto ya tirado en la tómbola'}>
                                      <span>{busy ? <CircularProgress size={16} /> : <Checkbox size="small" color="success" checked={Boolean(s.depositedAt)} onChange={() => toggleDeposited(s)} />}</span>
                                    </Tooltip>
                                  ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                                </TableCell>
                                <TableCell align="right">
                                  <Stack direction="row" justifyContent="flex-end" spacing={0}>
                                    {s.status === 'pending' && <>
                                      <Tooltip title="Registrar pago (mostrador)"><IconButton size="small" color="success" disabled={busy} onClick={() => { setPayForm({ method: 'cash', note: '', amount: '' }); setPayDialog(s); }}><Iconify icon="eva:checkmark-circle-2-outline" /></IconButton></Tooltip>
                                      <Tooltip title="Enviar / reenviar link de pago"><IconButton size="small" disabled={busy} onClick={() => sendLink(s)}>{busy ? <CircularProgress size={16} /> : <Iconify icon="eva:paper-plane-outline" />}</IconButton></Tooltip>
                                      <Tooltip title="Copiar link de pago"><IconButton size="small" onClick={() => copy(saleUrl(s.token))}><Iconify icon="eva:copy-outline" /></IconButton></Tooltip>
                                    </>}
                                    {paid && <Tooltip title="Reenviar boletos (SMS + email)"><IconButton size="small" disabled={busy} onClick={() => resendReceipt(s)}>{busy ? <CircularProgress size={16} /> : <Iconify icon="eva:email-outline" />}</IconButton></Tooltip>}
                                    {(paid || s.status === 'pending') && <Tooltip title="Cancelar y liberar números"><IconButton size="small" color="error" disabled={busy} onClick={() => cancelSale(s)}><Iconify icon="eva:close-circle-outline" /></IconButton></Tooltip>}
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              )}

              {tab === 1 && (
                <CardContent>
                  <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" alignItems="center">
                    {[['#e8f5e9', '#2e7d32', 'Vendido'], ['#fff8e1', '#b26a00', 'Reservado / pendiente'], ['#fff', '#999', 'Libre'], ['#e3f2fd', '#1565c0', 'Rango en línea (libre)']].map(([bg, fg, label]) => (
                      <Stack key={label} direction="row" spacing={0.75} alignItems="center"><Box sx={{ width: 16, height: 16, bgcolor: bg, border: `1px solid ${fg}`, borderRadius: 0.5 }} /><Typography variant="caption">{label}</Typography></Stack>
                    ))}
                    <Typography variant="caption" color="text.secondary">· Clic en un boleto libre para venderlo; en uno vendido para ver la venta.</Typography>
                  </Stack>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(52px, 1fr))', gap: 0.5 }}>
                    {Array.from({ length: stats.total }, (_, i) => i + 1).map((n) => {
                      const t = ticketMap[n];
                      const inOnline = raffle.onlineFrom && n >= raffle.onlineFrom && n <= raffle.onlineTo;
                      const sold = t && t.status === 'sold'; const held = t && !sold;
                      const bg = sold ? '#e8f5e9' : held ? '#fff8e1' : inOnline ? '#e3f2fd' : '#fff';
                      const fg = sold ? '#2e7d32' : held ? '#b26a00' : inOnline ? '#1565c0' : '#999';
                      const s = t ? sales.find((x) => x.id === t.saleId) : null;
                      return (
                        <Tooltip key={n} title={t ? `#${n} · ${t.buyerName || 'sin nombre'}${t.phone ? ` · ${t.phone}` : ''} · ${sold ? 'vendido' : 'reservado'}` : `#${n} libre`}>
                          <Box onClick={() => (t ? (s && setDetail(s)) : raffle.status !== 'closed' && openSale([n]))}
                            sx={{ textAlign: 'center', fontSize: 12, py: 0.6, borderRadius: 0.75, cursor: 'pointer', bgcolor: bg, color: fg, border: `1px solid ${fg}`, fontWeight: sold ? 700 : 400, userSelect: 'none' }}>
                            {n}
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </CardContent>
              )}

              {tab === 2 && (
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined"><CardHeader title="Resumen" /><CardContent>
                        {[
                          ['Boletos en total', stats.total], ['Vendidos (pagados)', stats.sold], ['Reservados sin pagar', stats.held], ['Libres', stats.available],
                          ['Vendidos en mostrador', stats.bySource.admin], ['Vendidos en línea', stats.bySource.online],
                          ['Ya en la tómbola', stats.deposited], ['Pagados que faltan por tirar', Math.max(0, stats.sold - stats.deposited)],
                          ['Cobrado', money(stats.collected)], ['A precio de lista', money(stats.expected)], ['Potencial si se vende todo', money(stats.total * price * 100)],
                        ].map(([k, v]) => <Stack key={k} direction="row" justifyContent="space-between" py={0.5} sx={{ borderBottom: '1px dashed #eee' }}><Typography variant="body2">{k}</Typography><Typography variant="body2" fontWeight="bold">{v}</Typography></Stack>)}
                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="subtitle2" gutterBottom>Cobrado por método</Typography>
                        {Object.keys(stats.byMethod).length === 0 && <Typography variant="body2" color="text.secondary">Nada cobrado todavía.</Typography>}
                        {Object.keys(stats.byMethod).map((m) => <Stack key={m} direction="row" justifyContent="space-between" py={0.5}><Typography variant="body2">{METHOD_LABELS[m] || m}</Typography><Typography variant="body2" fontWeight="bold">{money(stats.byMethod[m])}</Typography></Stack>)}
                      </CardContent></Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined"><CardHeader title="Boletos vendidos por día" /><CardContent>
                        {Object.keys(stats.dayMap).length === 0 && <Typography variant="body2" color="text.secondary">Sin ventas pagadas.</Typography>}
                        {Object.keys(stats.dayMap).sort().reverse().map((d) => (
                          <Stack key={d} direction="row" alignItems="center" spacing={1} py={0.5}>
                            <Typography variant="body2" sx={{ width: 90 }}>{moment(d).format('D MMM')}</Typography>
                            <Box sx={{ flex: 1, bgcolor: '#f0f0f0', borderRadius: 1, height: 14 }}><Box sx={{ width: `${Math.min(100, (stats.dayMap[d] / Math.max(1, Math.max(...Object.values(stats.dayMap)))) * 100)}%`, bgcolor: '#d40100', height: '100%', borderRadius: 1 }} /></Box>
                            <Typography variant="body2" fontWeight="bold" sx={{ width: 36, textAlign: 'right' }}>{stats.dayMap[d]}</Typography>
                          </Stack>
                        ))}
                      </CardContent></Card>
                    </Grid>
                  </Grid>
                </CardContent>
              )}
            </Card>
          </>
        )}
      </Container>

      {/* ---- Editor de sorteo ---- */}
      <Dialog open={editorOpen} onClose={() => !saving && setEditorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Editar sorteo' : 'Nuevo sorteo'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}><TextField fullWidth label="Nombre del sorteo *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Sorteo Krieghoff K-80" /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth select label="Estado" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}><MenuItem value="active">Activo</MenuItem><MenuItem value="closed">Cerrado (no se vende más)</MenuItem></TextField></Grid>
            <Grid item xs={12}><TextField fullWidth label="Premio" value={form.prize} onChange={(e) => setForm({ ...form, prize: e.target.value })} placeholder="Krieghoff K-80 Celtic Scroll II 32″ Parcours RH · valor $22,000" /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Descripción (se muestra en la página de venta)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth type="date" label="Fecha del sorteo" InputLabelProps={{ shrink: true }} value={form.drawDate} onChange={(e) => setForm({ ...form, drawDate: e.target.value })} /></Grid>
            <Grid item xs={6} md={5}><TextField fullWidth label="Lugar" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} placeholder="Hacienda Ana Paola" /></Grid>
            <Grid item xs={6} md={2}><TextField fullWidth type="number" label="Precio por boleto ($)" value={form.ticketPrice} onChange={(e) => setForm({ ...form, ticketPrice: e.target.value })} disabled={Boolean(editing) && tickets.length > 0} /></Grid>
            <Grid item xs={6} md={2}><TextField fullWidth type="number" label="Cantidad de boletos" value={form.totalTickets} onChange={(e) => setForm({ ...form, totalTickets: e.target.value })} /></Grid>
            <Grid item xs={12}><Divider><Typography variant="caption">Boleto virtual</Typography></Divider></Grid>
            <Grid item xs={12} md={3}><TextField fullWidth label="Marca (arriba del título)" value={form.ticketBrand || ''} onChange={(e) => setForm({ ...form, ticketBrand: e.target.value })} placeholder="KRIEGHOFF" /></Grid>
            <Grid item xs={12} md={3}><TextField fullWidth label="Título" value={form.ticketHeadline || ''} onChange={(e) => setForm({ ...form, ticketHeadline: e.target.value })} placeholder="BOLETO SORTEO" /></Grid>
            <Grid item xs={6} md={2}><TextField fullWidth type="number" label="Valor del premio ($)" value={form.prizeValue || ''} onChange={(e) => setForm({ ...form, prizeValue: e.target.value })} placeholder="22000" /></Grid>
            <Grid item xs={6} md={4}><TextField fullWidth label="Pie del talón" value={form.ticketFooter || ''} onChange={(e) => setForm({ ...form, ticketFooter: e.target.value })} /></Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} alignItems="center">
                {form.imageUrl ? <Box component="img" src={form.imageUrl} alt="" sx={{ height: 60, maxWidth: 200, objectFit: 'contain', border: '1px solid #eee', borderRadius: 1 }} /> : <Typography variant="body2" color="text.secondary">Sin foto del premio.</Typography>}
                <Button size="small" variant="outlined" component="label" disabled={uploadingImage}>{uploadingImage ? <CircularProgress size={16} /> : (form.imageUrl ? 'Cambiar foto' : 'Subir foto del premio')}<input hidden type="file" accept="image/*" onChange={(e) => { uploadPrizeImage(e.target.files && e.target.files[0]); e.target.value = ''; }} /></Button>
                {form.imageUrl && <Button size="small" color="error" onClick={() => setForm({ ...form, imageUrl: '' })}>Quitar</Button>}
                <Typography variant="caption" color="text.secondary">PNG con fondo transparente se ve mejor. Sale en el boleto y en la página de venta.</Typography>
              </Stack>
            </Grid>
            <Grid item xs={12}><Divider><Typography variant="caption">Venta en línea</Typography></Divider></Grid>
            <Grid item xs={12}><Typography variant="body2" color="text.secondary">Los boletos físicos se venden a mano. Para que la venta en línea no repita números, reserva un rango exclusivo para internet (p. ej. del 401 al 500). El sistema asigna los próximos libres de ese rango.</Typography></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth type="number" label="Desde #" value={form.onlineFrom} onChange={(e) => setForm({ ...form, onlineFrom: e.target.value })} /></Grid>
            <Grid item xs={6} md={3}><TextField fullWidth type="number" label="Hasta #" value={form.onlineTo} onChange={(e) => setForm({ ...form, onlineTo: e.target.value })} /></Grid>
            <Grid item xs={12} md={6}><FormControlLabel control={<Switch checked={form.onlineEnabled !== false} onChange={(e) => setForm({ ...form, onlineEnabled: e.target.checked })} />} label="Permitir compra en línea (página pública)" /></Grid>
            <Grid item xs={12}><Divider><Typography variant="caption">Textos y contacto</Typography></Divider></Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Condiciones (van en la página y en los emails)" value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} /></Grid>
            <Grid item xs={12}><Typography variant="body2" color="text.secondary">Reglas y condiciones completas: una regla por línea. Salen en la página pública en un acordeón (español / inglés) y el comprador tiene que marcar que las acepta antes de pagar.</Typography></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth multiline minRows={8} maxRows={18} label="Reglas y condiciones (español)" value={form.rulesEs || ''} onChange={(e) => setForm({ ...form, rulesEs: e.target.value })} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth multiline minRows={8} maxRows={18} label="Rules and conditions (English)" value={form.rulesEn || ''} onChange={(e) => setForm({ ...form, rulesEn: e.target.value })} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="Contacto" value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="Teléfono de contacto" value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="Email de contacto" value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={saveRaffle} disabled={saving}>{saving ? <CircularProgress size={20} /> : 'Guardar'}</Button>
        </DialogActions>
      </Dialog>

      {/* ---- Vender boletos ---- */}
      <Dialog open={saleOpen} onClose={() => !saving && setSaleOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Vender boletos{raffle ? ` · ${raffle.name}` : ''}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField fullWidth label="Números de boleto *" value={sale.numbersText} onChange={(e) => setSale({ ...sale, numbersText: e.target.value })} placeholder="312, 315-318, 441" helperText={saleNumbers.length ? `${saleNumbers.length} boleto${saleNumbers.length > 1 ? 's' : ''}: ${numbersLabel(saleNumbers)} · ${money(saleNumbers.length * price * 100)}` : 'Separa con comas; usa guion para un rango (315-318).'} autoFocus />
            <ToggleButtonGroup exclusive fullWidth size="small" value={sale.mode} onChange={(_, v) => v && setSale({ ...sale, mode: v })}>
              <ToggleButton value="paid">Ya pagó (mostrador)</ToggleButton>
              <ToggleButton value="link">Enviar link de pago</ToggleButton>
            </ToggleButtonGroup>
            <TextField fullWidth label="Nombre del comprador" value={sale.name} onChange={(e) => setSale({ ...sale, name: e.target.value })} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField fullWidth label={`Teléfono${sale.mode === 'link' ? ' (SMS)' : ''}`} value={sale.phone} onChange={(e) => setSale({ ...sale, phone: e.target.value })} />
              <TextField fullWidth label="Email" value={sale.email} onChange={(e) => setSale({ ...sale, email: e.target.value })} />
            </Stack>
            {sale.mode === 'paid' ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField fullWidth select label="Método de pago" value={sale.method} onChange={(e) => setSale({ ...sale, method: e.target.value })}>
                  {Object.keys(METHOD_LABELS).map((m) => <MenuItem key={m} value={m}>{METHOD_LABELS[m]}</MenuItem>)}
                </TextField>
                <TextField fullWidth type="number" label="Monto cobrado ($)" value={sale.amount} onChange={(e) => setSale({ ...sale, amount: e.target.value })} placeholder={(saleNumbers.length * price).toFixed(2)} helperText="Vacío = precio de lista" />
              </Stack>
            ) : (
              <Alert severity="info">Los números quedan reservados a nombre del comprador y le llega un link por SMS y email para pagar {money(saleAmount * 100)} con tarjeta. Cuando pague, recibe sus boletos automáticamente.</Alert>
            )}
            <TextField fullWidth label="Nota (opcional)" value={sale.note} onChange={(e) => setSale({ ...sale, note: e.target.value })} />
            {sale.mode === 'paid' && (sale.phone || sale.email) && <Typography variant="caption" color="text.secondary">Al guardar le enviamos los números por {[sale.phone && 'SMS', sale.email && 'email'].filter(Boolean).join(' y ')}.</Typography>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaleOpen(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={submitSale} disabled={saving || !saleNumbers.length}>{saving ? <CircularProgress size={20} /> : sale.mode === 'paid' ? 'Registrar venta' : 'Reservar y enviar link'}</Button>
        </DialogActions>
      </Dialog>

      {/* ---- Registrar pago de una venta pendiente ---- */}
      <Dialog open={Boolean(payDialog)} onClose={() => !saving && setPayDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Registrar pago · {payDialog ? numbersLabel(payDialog.numbers) : ''}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2">{payDialog?.buyerName} · {money(payDialog?.amountCents)}</Typography>
            <TextField fullWidth select label="Método" value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })}>
              {Object.keys(METHOD_LABELS).map((m) => <MenuItem key={m} value={m}>{METHOD_LABELS[m]}</MenuItem>)}
            </TextField>
            <TextField fullWidth type="number" label="Monto ($)" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} placeholder={((payDialog?.amountCents || 0) / 100).toFixed(2)} helperText="Vacío = monto de la venta" />
            <TextField fullWidth label="Nota" value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayDialog(null)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={submitPay} disabled={saving}>{saving ? <CircularProgress size={20} /> : 'Registrar pago'}</Button>
        </DialogActions>
      </Dialog>

      {/* ---- Detalle ---- */}
      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        {detail && (
          <>
            <DialogTitle>Venta · {numbersLabel(detail.numbers)}</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center"><Chip size="small" label={SALE_STATUS_LABELS[detail.status] || detail.status} color={SALE_STATUS_COLORS[detail.status] || 'default'} /><Chip size="small" variant="outlined" label={SOURCE_LABELS[detail.source] || detail.source} /></Stack>
                <Typography variant="body2"><strong>Comprador:</strong> {detail.buyerName || '—'}</Typography>
                <Typography variant="body2"><strong>Teléfono:</strong> {detail.phone || '—'} · <strong>Email:</strong> {detail.email || '—'}</Typography>
                <Typography variant="body2"><strong>Monto:</strong> {money(detail.amountCents)} ({(detail.numbers || []).length} × {money(detail.unitCents || price * 100)})</Typography>
                <Typography variant="body2"><strong>Creado:</strong> {moment(toDate(detail.createdAt)).format('D/M/YYYY h:mm a')}{detail.soldBy ? ` · ${detail.soldBy}` : ''}</Typography>
                {detail.note && <Typography variant="body2"><strong>Nota:</strong> {detail.note}</Typography>}
                {detail.status === 'paid' && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2"><strong>Tómbola:</strong> {detail.depositedAt ? `tirado el ${moment(toDate(detail.depositedAt)).format('D/M/YYYY h:mm a')}${detail.depositedBy ? ` · ${detail.depositedBy}` : ''}` : 'todavía no se ha tirado'}</Typography>
                    <Button size="small" variant={detail.depositedAt ? 'text' : 'outlined'} color={detail.depositedAt ? 'inherit' : 'success'} disabled={busyId === detail.id} onClick={() => toggleDeposited(detail)}>{detail.depositedAt ? 'Quitar marca' : 'Marcar como tirado'}</Button>
                  </Stack>
                )}
                {detail.status === 'pending' && <Typography variant="body2"><strong>Link de pago:</strong> <a href={saleUrl(detail.token)} target="_blank" rel="noreferrer">{saleUrl(detail.token)}</a>{detail.linkSentAt ? ` · enviado ${detail.linkSentCount || 1} vez${(detail.linkSentCount || 1) > 1 ? 'es' : ''}` : ' · no enviado'}</Typography>}
                {detail.cancelNote && <Typography variant="body2" color="error"><strong>Cancelación:</strong> {detail.cancelNote}</Typography>}
                {(detail.payments || []).length > 0 && <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2">Pagos</Typography>
                  {detail.payments.map((p, i) => <Typography key={i} variant="body2">{moment(toDate(p.paidAt)).format('D/M/YYYY h:mm a')} · {METHOD_LABELS[p.method] || p.method}{p.last4 ? ` ···· ${p.last4}` : ''} · {money(p.amountCents)}{p.recordedBy ? ` · ${p.recordedBy}` : ''}{p.note ? ` · ${p.note}` : ''}</Typography>)}
                </>}
                {detail.receiptResult && <Typography variant="caption" color="text.secondary">Boletos enviados: {detail.receiptResult.smsSent ? 'SMS ✓' : 'SMS ✗'} · {detail.receiptResult.emailSent ? 'email ✓' : 'email ✗'}</Typography>}
                {(detail.status === 'paid' || detail.status === 'pending') && raffle && <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2">Boletos virtuales{detail.status === 'pending' ? ' (pendiente de pago)' : ''}</Typography>
                  <TicketGallery raffle={raffle} numbers={detail.numbers || []} buyer={{ name: detail.buyerName, phone: detail.phone, email: detail.email, address: detail.address }} raffleName={raffle.name} />
                </>}
              </Stack>
            </DialogContent>
            <DialogActions>
              {detail.status === 'paid' && <Button onClick={() => resendReceipt(detail)}>Reenviar boletos</Button>}
              {detail.status === 'pending' && <Button onClick={() => sendLink(detail)}>Reenviar link</Button>}
              {(detail.status === 'paid' || detail.status === 'pending') && <Button color="error" onClick={() => cancelSale(detail)}>Cancelar venta</Button>}
              <Box flex={1} />
              <Button onClick={() => setDetail(null)}>Cerrar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)} variant="filled">{toast.message}</Alert> : null}
      </Snackbar>
    </>
  );
}
